import { useMemo, useState } from "react";
import { api, ApiError } from "../../../lib/api";
import { ConfirmButton } from "../../../components/ConfirmButton";
import type { Asset } from "../../../lib/resources";
import type { SafedTestItem, SafedTestInput, SafedStatus } from "../../../lib/api";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../../components/ModalShell";
import { Button } from "../../../components/Button";
import { StatusPill } from "../../../components/StatusPill";
import { Loader } from "../../../components/Loader";
import { formatDate } from "../../../lib/format";

// SAFed supplementary testing on the lift record. SAFed tracking sits ALONGSIDE the LOLER
// thorough examination: a lift carries one row per scheduled supplementary test (passenger
// supplementary 1/5/10 year, non-passenger safety gear 5-yearly, hydraulic rupture valve
// SAFed A18). The interval is editable per record; the platform presents it as the
// scheduled interval, not a legal mandate. Self-contained modal (mirrors CertificatesButton).

// A datalist hint of the known SAFed test types (free text; the office may add others).
const KNOWN_SAFED_TEST_TYPES = [
  "Passenger lift supplementary test",
  "Non-passenger lift safety gear test",
  "Hydraulic lift rupture valve test (SAFed A18)",
];

// Common intervals as quick presets (months). The field stays editable.
const INTERVAL_PRESETS = [
  { months: 12, label: "1 year" },
  { months: 60, label: "5 years" },
  { months: 120, label: "10 years" },
];

const intervalLabel = (months: number) => {
  if (months % 12 === 0) {
    const years = months / 12;
    return `${years} year${years === 1 ? "" : "s"}`;
  }
  return `${months} month${months === 1 ? "" : "s"}`;
};

// The SAFed status as the registry's serviceDue key (the domain this and the Last serviced cell share).
// A SAFed test's status says "due-soon" with a hyphen where the registry key has an underscore, so that
// one value is mapped. Callers render <StatusPill domain="serviceDue" status={safedDueStatus(s)} variant="solid" />:
// the grey no-date fill is the themed neutral and the red is the fixed #c0392b (design system Session 5).
export function safedDueStatus(status: SafedStatus): string {
  return status === "due-soon" ? "due_soon" : status;
}

interface FormState {
  id: string | null; // null = creating
  testType: string;
  lastTestDate: string;
  intervalMonths: string;
  certificateRef: string;
  certificateUrl: string;
  notes: string;
}

const emptyForm: FormState = {
  id: null,
  testType: "",
  lastTestDate: "",
  intervalMonths: "12",
  certificateRef: "",
  certificateUrl: "",
  notes: "",
};

const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green";
const labelCls = "mb-1 block text-sm text-ele-body";

export function SafedTestsButton({ asset, onChanged }: { asset: Asset; onChanged?: () => void }) {
  const [open, setOpen] = useState(false);
  const [tests, setTests] = useState<SafedTestItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const datalistId = useMemo(() => `safed-types-${asset.id}`, [asset.id]);

  async function openModal() {
    setOpen(true);
    setError(null);
    setForm(emptyForm);
    setLoading(true);
    try {
      setTests(await api.assets.safedTests(asset.id));
    } catch {
      setError("Could not load the SAFed tests for this lift.");
    } finally {
      setLoading(false);
    }
  }

  function startEdit(t: SafedTestItem) {
    setError(null);
    setForm({
      id: t.id,
      testType: t.testType,
      lastTestDate: t.lastTestDate ? t.lastTestDate.slice(0, 10) : "",
      intervalMonths: String(t.intervalMonths),
      certificateRef: t.certificateRef ?? "",
      certificateUrl: "", // never round-tripped from the server (URL not exposed); re-enter to change
      notes: t.notes ?? "",
    });
  }

  async function save() {
    if (!form.testType.trim()) {
      setError("Enter the test type.");
      return;
    }
    const months = Number(form.intervalMonths);
    if (!Number.isFinite(months) || months <= 0) {
      setError("Enter the interval in months.");
      return;
    }
    setBusy(true);
    setError(null);
    const body: SafedTestInput = {
      testType: form.testType.trim(),
      lastTestDate: form.lastTestDate ? new Date(`${form.lastTestDate}T00:00:00Z`).toISOString() : null,
      intervalMonths: Math.trunc(months),
      certificateRef: form.certificateRef.trim() || null,
      certificateUrl: form.certificateUrl.trim() || null,
      notes: form.notes.trim() || null,
    };
    try {
      if (form.id) {
        const updated = await api.assets.updateSafedTest(asset.id, form.id, body);
        setTests((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      } else {
        const created = await api.assets.createSafedTest(asset.id, body);
        setTests((prev) => [created, ...prev]);
      }
      setForm(emptyForm);
      onChanged?.();
    } catch (err) {
      setError(err instanceof ApiError && err.code ? "Could not save the test. Check the fields and try again." : "Could not save the test. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    setBusy(true);
    setError(null);
    try {
      await api.assets.deleteSafedTest(asset.id, id);
      setTests((prev) => prev.filter((t) => t.id !== id));
      if (form.id === id) setForm(emptyForm);
      onChanged?.();
    } catch {
      setError("Could not delete the test. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <button
        type="button"
        onClick={openModal}
        className="rounded-ele border border-ele-blue px-3 py-1 font-menu text-xs tracking-wide text-ele-blue transition hover:bg-ele-blue/10"
      >
        SAFed
      </button>

      {open && (
        <ModalShell ariaLabel="SAFed supplementary tests" onClose={() => !busy && setOpen(false)} width="max-w-lg" panelClassName="max-h-[90vh] overflow-y-auto p-6 text-left">
            <h2 className="mb-1 font-heading text-xl text-ele-strong">SAFed supplementary tests</h2>
            <p className="mb-2 text-sm text-ele-body">
              {asset.eleNumber ?? "Lift"}
              {asset.site?.name ? `, ${asset.site.name}` : ""}
            </p>
            <p className="mb-4 text-xs text-ele-body opacity-80">
              Supplementary testing sits alongside the LOLER thorough examination. The interval shown is the
              scheduled interval for the test and can be edited per record. This is not legal advice; set the
              interval against the current SAFed guidance.
            </p>

            <h3 className="mb-2 font-menu text-xs uppercase tracking-wider text-ele-blue">On file</h3>
            {loading ? (
              <Loader shape="inline" />
            ) : tests.length === 0 ? (
              <p className="text-sm text-ele-body opacity-70">No SAFed tests recorded yet.</p>
            ) : (
              <ul className="mb-4 space-y-2">
                {tests.map((t) => (
                  <li key={t.id} className="rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-ele-strong">{t.testType}</span>
                      <StatusPill domain="serviceDue" status={safedDueStatus(t.status)} variant="solid" />
                    </div>
                    <div className="mt-1 text-ele-body">
                      Interval {intervalLabel(t.intervalMonths)}
                      {t.lastTestDate ? `, last tested ${formatDate(t.lastTestDate)}` : ", no last test recorded"}
                      {t.nextDueDate ? `, next due ${formatDate(t.nextDueDate)}` : ""}
                    </div>
                    {t.certificateRef ? <div className="text-ele-body opacity-80">Certificate ref: {t.certificateRef}</div> : null}
                    {t.notes ? <div className="text-ele-body opacity-80">{t.notes}</div> : null}
                    <div className="mt-2 flex gap-3">
                      <button type="button" onClick={() => startEdit(t)} className="text-xs tracking-wide text-ele-blue hover:underline">
                        Edit
                      </button>
                      <ConfirmButton onConfirm={() => remove(t.id)} label="Delete" busy={busy} className="text-xs tracking-wide text-ele-danger hover:underline" />
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <h3 className="mb-2 mt-4 font-menu text-xs uppercase tracking-wider text-ele-blue">
              {form.id ? "Edit test" : "Add a test"}
            </h3>
            <div className="space-y-3">
              <div>
                <label className={labelCls} htmlFor="safed-type">Test type</label>
                <input
                  id="safed-type"
                  list={datalistId}
                  value={form.testType}
                  onChange={(e) => setForm((f) => ({ ...f, testType: e.target.value }))}
                  className={inputCls}
                  placeholder="e.g. Hydraulic lift rupture valve test (SAFed A18)"
                />
                <datalist id={datalistId}>
                  {KNOWN_SAFED_TEST_TYPES.map((o) => (
                    <option key={o} value={o} />
                  ))}
                </datalist>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className={labelCls} htmlFor="safed-last">Last test date</label>
                  <input
                    id="safed-last"
                    type="date"
                    value={form.lastTestDate}
                    onChange={(e) => setForm((f) => ({ ...f, lastTestDate: e.target.value }))}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="safed-interval">Interval (months)</label>
                  <input
                    id="safed-interval"
                    type="number"
                    min={1}
                    value={form.intervalMonths}
                    onChange={(e) => setForm((f) => ({ ...f, intervalMonths: e.target.value }))}
                    className={inputCls}
                  />
                  <div className="mt-1 flex flex-wrap gap-1">
                    {INTERVAL_PRESETS.map((p) => (
                      <button
                        key={p.months}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, intervalMonths: String(p.months) }))}
                        className="rounded-ele border border-ele-purple-accent px-2 py-0.5 text-[11px] text-ele-body hover:text-ele-strong"
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <label className={labelCls} htmlFor="safed-ref">Certificate reference (optional)</label>
                <input
                  id="safed-ref"
                  type="text"
                  value={form.certificateRef}
                  onChange={(e) => setForm((f) => ({ ...f, certificateRef: e.target.value }))}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="safed-url">Certificate link (optional)</label>
                <input
                  id="safed-url"
                  type="url"
                  value={form.certificateUrl}
                  onChange={(e) => setForm((f) => ({ ...f, certificateUrl: e.target.value }))}
                  className={inputCls}
                  placeholder="Drive link to the test certificate"
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="safed-notes">Notes (optional)</label>
                <textarea
                  id="safed-notes"
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  rows={2}
                  className={inputCls}
                />
              </div>
              {error && <p role="alert" className="text-sm text-ele-danger">{error}</p>}
            </div>

            <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
              {form.id && (
                <button
                  type="button"
                  onClick={() => setForm(emptyForm)}
                  className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body ${MODAL_ACTION_BTN}`}
                >
                  Cancel edit
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body ${MODAL_ACTION_BTN}`}
              >
                Close
              </button>
              <Button size="sm" className={MODAL_ACTION_BTN} onClick={save} disabled={busy || !form.testType.trim()}>
                {busy ? "Saving..." : form.id ? "Save changes" : "Add test"}
              </Button>
            </div>
        </ModalShell>
      )}
    </div>
  );
}
